import { useMutation } from '@apollo/client';
import styled from '@emotion/styled';
import { useResource } from '@landsbankinn/l16n';
import { Button, TextInput, theme } from '@landsbankinn/ui';
import { LandsbankinnContext } from '@landsbankinn/utils';
import { isSameDay, isSameMinute, startOfDay } from 'date-fns';
import React, { useContext, useEffect, useState } from 'react';
import { animated, useTransition } from 'react-spring';
import { ConsultationType } from '../../../../../__generated__/globalTypes';
import type { availableDatesAndTimes } from '../../../../graphql/Appointment/__generated__/availableDatesAndTimes';
import type {
  ReserveAppointment as IReserveAppointment,
  ReserveAppointmentVariables as IReserveAppointmentVariables,
} from '../../../../graphql/Appointment/__generated__/ReserveAppointment';
import { RESERVE_APPOINTMENT } from '../../../../graphql/Appointment/reserveAppointment';
import { FormContainer } from '../../../book-appointment/shared/FormContainer';

import { AppointmentStep, useAppointmentContext } from '../../BookAppointment';
import { DateInput } from './DateInput';
import res from './SelectDateAndTime.res';
import { TimeDropdown } from './TimeDropdown';

enum DateStep {
  SelectDate = 'selectDate',
  SelectTime = 'selectTime',
  Note = 'note',
}

const StyledButton = styled(Button)`
  width: 100%;
`;

const InputContainer = styled(animated.div)`
  margin-bottom: 20px;
`;

const InputsContainer = styled.div`
  min-height: 412px;
`;

interface IProps {
  data?: availableDatesAndTimes;
}

export function SelectDateAndTime({ data }: IProps) {
  const { state, setCurrentStep, dispatch } = useAppointmentContext();
  const { r } = useResource();
  const { service, serviceType } = state;
  const { client } = useContext(LandsbankinnContext);
  const [steps, setSteps] = useState<DateStep[]>([DateStep.SelectDate]);
  const [selectedTime, setSelectedTime] = useState<number | undefined>(
    undefined,
  );

  useEffect(() => {
    if (
      state.date &&
      state.date?.getHours() > 0 &&
      data &&
      data.node?.__typename === 'ServiceLocation'
    ) {
      const selectedTimeIndex = data.node.availableAppointments
        ?.find((i) => isSameDay(new Date(i.date), state.date as Date))
        ?.time.findIndex((t) => {
          const [hour, minute] = t.split(':');
          return (
            Number(hour) === state.date?.getHours() &&
            Number(minute) === state.date?.getMinutes()
          );
        });
      setSelectedTime(selectedTimeIndex);
    }
  }, [data, state.date, setSelectedTime]);

  const [reserveAppointment, { loading: reserveLoading }] = useMutation<
    IReserveAppointment,
    IReserveAppointmentVariables
  >(RESERVE_APPOINTMENT, {
    client,
    onCompleted: (data) => {
      if (setCurrentStep) {
        dispatch?.({
          type: 'setReserveAppointmentId',
          id: data.reserveAppointment.id,
        });
        setCurrentStep(AppointmentStep.CustomerInfo);
      }
    },
  });

  const transitions = useTransition(steps, {
    config: { mass: 2, tension: 2000, friction: 200 },
    from: { opacity: 0, x: '508px' },
    enter: { opacity: 1, x: '0px' },
    leave: { opacity: 0, x: '508px' },
  });

  useEffect(() => {
    const newSteps = [DateStep.SelectDate];
    if (state.date) {
      newSteps.push(DateStep.SelectTime);
    }

    if (
      (state.date && !isSameMinute(startOfDay(state.date), state.date)) ||
      state.note
    ) {
      newSteps.push(DateStep.Note);
    }

    setSteps(newSteps);
  }, [state, data, setSteps]);

  return (
    <FormContainer
      onBack={() => setCurrentStep?.(AppointmentStep.SelectService)}
      onSubmit={() => {
        // user has navigate back from the next step and did not make any changes we use the same reservation
        if (state.reserveAppointmentId) {
          setCurrentStep?.(AppointmentStep.CustomerInfo);
          return;
        }
        reserveAppointment({
          variables: {
            date: state.date,
            serviceLocationId: state.serviceLocationId ?? '',
            consultationServiceServiceId: service?.id ?? '',
            consultationType: serviceType ?? ConsultationType.Appointment,
          },
        });
      }}
    >
      <>
        <InputsContainer>
          {transitions(({ x, ...rest }, item) => (
            <InputContainer
              key={item}
              style={{
                ...rest,
                transform: x.to((x) =>
                  x === '0px' ? 'initial' : `translate3d(${x},0,0)`,
                ),
              }}
            >
              {item === DateStep.SelectDate && (
                <DateInput
                  setSelectedTime={setSelectedTime as any}
                  availableDates={
                    data?.node?.__typename === 'ServiceLocation'
                      ? data.node.availableAppointments?.map(
                          (i) => new Date(i.date),
                        )
                      : undefined
                  }
                />
              )}

              {data?.node?.__typename === 'ServiceLocation' &&
                item === DateStep.SelectTime && (
                  <TimeDropdown
                    data={data.node}
                    selectedTime={selectedTime}
                    setSelectedTime={setSelectedTime as any}
                  />
                )}

              {item === DateStep.Note && (
                <TextInput
                  inputSize="small"
                  setHeight="136px"
                  placeholder={r(res.comment)}
                  value={state.note}
                  onChange={(e) => {
                    if (dispatch) {
                      dispatch({ type: 'setNote', note: e.target.value });
                    }
                  }}
                />
              )}
            </InputContainer>
          ))}
        </InputsContainer>

        <StyledButton
          loading={reserveLoading}
          title={r(res.continue)}
          type="submit"
          bg={theme.colors.primary}
        />
      </>
    </FormContainer>
  );
}
